/**
 * A menu entry can carry several features that hold the same answer. Locations that run
 * parallel question sets - one per user group, e.g. "Kantine oder zum Mitnehmen? (intern)"
 * next to "... (Extern)" - store that answer once per question, and the waiter UI only ever
 * renders the values, never the question. The label then read "Kantine, Kantine, 12 Uhr,
 * 12 Uhr, Menü Normal".
 *
 * Repeats of one value are folded into a single entry. The amount kept is the largest of the
 * folded ones rather than their sum: the same option cannot be picked twice, so a repeat is a
 * second question answered the same way, not a second portion. An explicitly multiplied pick
 * (amount > 1) keeps its count.
 */
export function foldFeatureValues(features) {
  const folded = [];
  const positionByValue = new Map();

  (features || []).forEach((feature) => {
    const value = (feature.value || '').trim();

    if (!value) {
      return;
    }

    const amount = feature.amount || 1;
    const position = positionByValue.get(value);

    if (typeof position === 'undefined') {
      positionByValue.set(value, folded.length);
      folded.push({ ...feature, value, amount });
      return;
    }

    if (amount > folded[position].amount) {
      folded[position] = { ...folded[position], amount };
    }
  });

  return folded;
}

const toFeatureLabel = feature => (feature.amount > 1 ? `${feature.amount}x ${feature.value}` : feature.value);

/**
 * The feature labels of one order entry, as the sidebar, the article list and the printed
 * article list show them: selected features first, then variant values, then what the guest
 * deselected. Duplicates are folded away (see foldFeatureValues).
 */
export function buildFeatureLabels(entry) {
  const features = (entry && entry.features) || [];
  const productVariants = (entry && entry.productVariants) || [];

  const selected = foldFeatureValues(features.filter(x => x.value && !x.isDeselectFeature))
    .map(toFeatureLabel);

  const variantValues = productVariants
    .map(variant => variant.values.map(value => value.variantValue))
    .reduce((a, b) => a.concat(b), []);

  const deselected = foldFeatureValues(features.filter(x => x.value && x.isDeselectFeature && x.deselectedByUser))
    .map(feature => `Abwahl: ${feature.value}`);

  const labels = [...selected, ...variantValues, ...deselected];

  // a variant that repeats a feature value would otherwise slip through the fold above
  return labels.filter((label, index) => labels.indexOf(label) === index);
}
